import React, { useEffect, useState, useContext } from 'react';
import axios from 'axios';
import { API, AuthContext } from '../App';
import Layout from '../components/Layout';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Hammer, Plus, FileText, Sparkles, Trash2, ImagePlus, CheckCircle2 } from 'lucide-react';
import { toast } from 'sonner';
import BuildingFilter from '../components/BuildingFilter';
import { generateJobOrderPDF } from '../lib/jobOrderPdf';

const WORK_TYPES = ['electrical', 'plumbing', 'hvac', 'civil', 'cleaning', 'painting', 'carpentry', 'working_at_height', 'hot_work', 'confined_space', 'fire_safety', 'landscaping', 'other'];
const STATUSES = ['draft', 'issued', 'in_progress', 'completed', 'reviewed', 'cancelled'];

const STATUS_COLOR = {
  draft: 'bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-200',
  issued: 'bg-sky-100 text-sky-700 dark:bg-sky-900/40 dark:text-sky-300',
  in_progress: 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300',
  completed: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300',
  reviewed: 'bg-violet-100 text-violet-700 dark:bg-violet-900/40 dark:text-violet-300',
  cancelled: 'bg-rose-100 text-rose-700 dark:bg-rose-900/40 dark:text-rose-300',
};

function riskCellColor(score) {
  if (score >= 16) return 'bg-rose-600 text-white';
  if (score >= 10) return 'bg-orange-500 text-white';
  if (score >= 5) return 'bg-amber-500 text-white';
  return 'bg-emerald-600 text-white';
}

export default function JobOrders() {
  const { user } = useContext(AuthContext);
  const [orders, setOrders] = useState([]);
  const [buildings, setBuildings] = useState([]);
  const [staff, setStaff] = useState([]);
  const [company, setCompany] = useState(null);
  const [buildingFilter, setBuildingFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [showNew, setShowNew] = useState(false);
  const [editing, setEditing] = useState(null);
  const [pdfStyle, setPdfStyle] = useState('modern');
  const [loading, setLoading] = useState(true);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const bQ = buildingFilter !== 'all' ? `&building_id=${buildingFilter}` : '';
      const sQ = statusFilter !== 'all' ? `&status=${statusFilter}` : '';
      const [o, b, st, c] = await Promise.all([
        axios.get(`${API}/job-orders?${bQ.slice(1)}${sQ}`, { withCredentials: true }),
        axios.get(`${API}/buildings`, { withCredentials: true }),
        axios.get(`${API}/staff`, { withCredentials: true }).catch(() => ({ data: [] })),
        axios.get(`${API}/companies`, { withCredentials: true }).then(r => r.data?.[0] || null).catch(() => null),
      ]);
      setOrders(o.data || []);
      setBuildings(b.data || []);
      setStaff(st.data || []);
      setCompany(c);
    } catch { toast.error('Failed to load Job Orders'); }
    finally { setLoading(false); }
  };

  // eslint-disable-next-line
  useEffect(() => { fetchAll(); }, [buildingFilter, statusFilter]);

  const downloadPdf = async (jo) => {
    try {
      await generateJobOrderPDF({ jo, company: company || {}, style: pdfStyle });
      toast.success(`Job Order PDF (${pdfStyle}) downloaded`);
    } catch (e) { toast.error('PDF generation failed: ' + e.message); }
  };

  const remove = async (id) => {
    if (!window.confirm('Delete this Job Order?')) return;
    try { await axios.delete(`${API}/job-orders/${id}`, { withCredentials: true }); toast.success('Deleted'); fetchAll(); }
    catch { toast.error('Failed'); }
  };

  return (
    <Layout>
      <div data-testid="job-orders-page" className="space-y-6">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h1 className="text-3xl font-bold text-foreground flex items-center gap-2">
              <Hammer className="w-7 h-7 text-orange-500" /> Job Orders
            </h1>
            <p className="text-sm text-muted-foreground mt-1">
              Formal printable work instructions with MoS, Risk Assessment, and before/after photos. AI-tweakable per work-type templates.
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <BuildingFilter value={buildingFilter} onChange={setBuildingFilter} buildings={buildings} />
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-[160px]" data-testid="jo-status-filter"><SelectValue placeholder="All statuses" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All statuses</SelectItem>
                {STATUSES.map(st => <SelectItem key={st} value={st}>{st}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={pdfStyle} onValueChange={setPdfStyle}>
              <SelectTrigger className="w-[140px]" data-testid="jo-pdf-style"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="classic">Classic PDF</SelectItem>
                <SelectItem value="modern">Modern PDF</SelectItem>
                <SelectItem value="compact">Compact PDF</SelectItem>
              </SelectContent>
            </Select>
            <Button data-testid="new-job-order-btn" onClick={() => setShowNew(true)}><Plus className="w-4 h-4 mr-1" /> New Job Order</Button>
          </div>
        </div>

        {loading ? <div className="text-muted-foreground text-sm">Loading…</div> :
          orders.length === 0 ? <Card className="p-8 text-center text-muted-foreground">No Job Orders yet. Click &ldquo;New Job Order&rdquo; to create your first formal work instruction.</Card> :
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
              {orders.map(jo => (
                <Card key={jo.id} data-testid={`jo-${jo.id}`} className="p-4">
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex-1 min-w-0">
                      <div className="font-mono text-xs text-orange-600 dark:text-orange-400 font-bold">{jo.jo_number}</div>
                      <div className="font-semibold text-foreground truncate">{jo.title}</div>
                      <div className="text-xs text-muted-foreground">{jo.building_name || '—'}</div>
                    </div>
                    <span className={`text-xs px-2 py-0.5 rounded-full uppercase ${STATUS_COLOR[jo.status]}`}>{jo.status}</span>
                  </div>
                  {jo.work_types?.length > 0 && (
                    <div className="mt-2 flex flex-wrap gap-1">
                      {jo.work_types.slice(0, 3).map(wt => <span key={wt} className="text-[10px] px-2 py-0.5 rounded-full bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-200 uppercase">{wt.replace(/_/g, ' ')}</span>)}
                      {jo.work_types.length > 3 && <span className="text-[10px] text-muted-foreground">+{jo.work_types.length - 3} more</span>}
                    </div>
                  )}
                  <div className="mt-2 text-xs text-muted-foreground">
                    Assigned: {jo.assigned_to_name || 'Unassigned'}{jo.assigned_to_company ? ` (${jo.assigned_to_company})` : ''}
                  </div>
                  {jo.related_lpo && <div className="text-xs text-muted-foreground">LPO: <span className="font-mono">{jo.related_lpo}</span></div>}
                  {(jo.risk_assessment || []).length > 0 && (
                    <div className="mt-2 text-xs">
                      <span className="text-muted-foreground">Max risk: </span>
                      {(() => {
                        const max = Math.max(...jo.risk_assessment.map(r => r.risk_score || 1));
                        return <span className={`inline-block px-2 py-0.5 rounded-full text-[10px] font-bold ${riskCellColor(max)}`}>{max}</span>;
                      })()}
                    </div>
                  )}
                  <div className="mt-3 flex flex-wrap gap-2">
                    <Button data-testid={`edit-jo-${jo.id}`} size="sm" variant="outline" onClick={() => setEditing(jo)}>Open</Button>
                    <Button data-testid={`pdf-jo-${jo.id}`} size="sm" variant="outline" onClick={() => downloadPdf(jo)}><FileText className="w-3.5 h-3.5 mr-1" />PDF</Button>
                    <Button data-testid={`delete-jo-${jo.id}`} size="sm" variant="ghost" onClick={() => remove(jo.id)} className="text-rose-600 ml-auto"><Trash2 className="w-3.5 h-3.5" /></Button>
                  </div>
                </Card>
              ))}
            </div>
        }
      </div>

      {showNew && <JobOrderDialog onClose={() => setShowNew(false)} onSaved={fetchAll} buildings={buildings} staff={staff} user={user} />}
      {editing && <JobOrderDialog jo={editing} onClose={() => setEditing(null)} onSaved={fetchAll} buildings={buildings} staff={staff} user={user} />}
    </Layout>
  );
}

// ─────────────────── EDIT / CREATE DIALOG ───────────────────
function JobOrderDialog({ jo, onClose, onSaved, buildings, staff }) {
  const isEdit = !!jo;
  const [form, setForm] = useState(jo ? { ...jo } : {
    title: '', scope: '', building_id: '', related_lpo: '', related_wo: '',
    assignee_type: 'staff', assigned_to_id: '', assigned_to_name: '', assigned_to_phone: '', assigned_to_company: '',
    work_types: [], safety_instructions: '', method_of_statement: '',
    risk_assessment: [], completion_checklist: [],
    before_photos: [], after_photos: [],
    scheduled_start: '', scheduled_end: '', status: 'draft',
  });
  const [saving, setSaving] = useState(false);
  const [aiLoading, setAiLoading] = useState(false);
  const [templates, setTemplates] = useState(null);

  useEffect(() => {
    let cancelled = false;
    axios.get(`${API}/job-orders/templates/list`, { withCredentials: true })
      .then(r => { if (!cancelled) setTemplates(r.data); }).catch(() => {});
    return () => { cancelled = true; };
  }, []);

  const toggleWorkType = (wt) => {
    setForm(f => ({ ...f, work_types: f.work_types.includes(wt) ? f.work_types.filter(x => x !== wt) : [...f.work_types, wt] }));
  };

  const loadTemplate = () => {
    if (!templates || !form.work_types.length) { toast.error('Pick a work type first'); return; }
    const wt = form.work_types[0];
    const t = templates.templates[wt] || templates.templates.other;
    setForm(f => ({
      ...f,
      safety_instructions: t.safety_instructions,
      method_of_statement: t.method_of_statement,
      risk_assessment: t.risk_assessment.map(r => ({ ...r })),
      completion_checklist: t.checklist.map(name => ({ name, done: false })),
    }));
    toast.success(`Loaded "${wt}" template`);
  };

  const aiTweak = async () => {
    setAiLoading(true);
    try {
      const r = await axios.post(`${API}/job-orders/ai/tweak`, {
        title: form.title, scope: form.scope, work_types: form.work_types,
        building_name: buildings.find(b => b.id === form.building_id)?.name,
      }, { withCredentials: true });
      setForm(f => ({
        ...f,
        safety_instructions: r.data.safety_instructions,
        method_of_statement: r.data.method_of_statement,
        risk_assessment: r.data.risk_assessment,
      }));
      toast.success(r.data.source === 'ai' ? 'AI customised the safety pack' : 'Loaded template (AI unavailable)');
    } catch { toast.error('AI tweak failed'); }
    finally { setAiLoading(false); }
  };

  const updateRisk = (i, field, val) => {
    const next = [...form.risk_assessment];
    const v = field.includes('likelihood') || field.includes('severity') ? Math.max(1, Math.min(5, parseInt(val) || 1)) : val;
    next[i] = { ...next[i], [field]: v };
    if (['likelihood', 'severity'].includes(field)) next[i].risk_score = (next[i].likelihood || 1) * (next[i].severity || 1);
    if (['residual_likelihood', 'residual_severity'].includes(field)) next[i].residual_score = (next[i].residual_likelihood || 1) * (next[i].residual_severity || 1);
    setForm({ ...form, risk_assessment: next });
  };

  const addRiskRow = () => setForm(f => ({ ...f, risk_assessment: [...f.risk_assessment, { hazard: '', likelihood: 1, severity: 1, risk_score: 1, control_measures: '', residual_likelihood: 1, residual_severity: 1, residual_score: 1 }] }));
  const removeRiskRow = (i) => setForm(f => ({ ...f, risk_assessment: f.risk_assessment.filter((_, idx) => idx !== i) }));

  const updateChecklistItem = (i, field, val) => {
    const next = [...form.completion_checklist]; next[i] = { ...next[i], [field]: val }; setForm({ ...form, completion_checklist: next });
  };
  const addChecklistItem = () => setForm(f => ({ ...f, completion_checklist: [...f.completion_checklist, { name: '', done: false }] }));

  const onPhoto = async (e, key) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    const dataUrls = await Promise.all(files.map(f => new Promise(res => { const r = new FileReader(); r.onloadend = () => res(r.result); r.readAsDataURL(f); })));
    setForm(f => ({ ...f, [key]: [...(f[key] || []), ...dataUrls] }));
  };
  const removePhoto = (key, i) => setForm(f => ({ ...f, [key]: f[key].filter((_, idx) => idx !== i) }));

  const save = async () => {
    if (!form.title) return toast.error('Title is required');
    if (!form.assigned_to_name) return toast.error('Assignee name is required');
    setSaving(true);
    try {
      if (isEdit) {
        await axios.put(`${API}/job-orders/${jo.id}`, form, { withCredentials: true });
        toast.success('Job Order updated');
      } else {
        await axios.post(`${API}/job-orders`, form, { withCredentials: true });
        toast.success('Job Order created');
      }
      onSaved(); onClose();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Save failed'); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle>{isEdit ? `Edit ${jo.jo_number}` : 'New Job Order'}</DialogTitle></DialogHeader>
        <div className="space-y-4 py-2">
          {/* Basics */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div className="md:col-span-2"><Label>Title *</Label><Input data-testid="jo-title" value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} placeholder="e.g. Replace AHU motor — Zone 3" /></div>
            <div className="md:col-span-2"><Label>Scope of work</Label><Textarea rows={3} value={form.scope || ''} onChange={e => setForm({ ...form, scope: e.target.value })} /></div>
            <div><Label>Building</Label>
              <Select value={form.building_id || 'none'} onValueChange={v => setForm({ ...form, building_id: v === 'none' ? '' : v })}>
                <SelectTrigger><SelectValue placeholder="Pick" /></SelectTrigger>
                <SelectContent><SelectItem value="none">None</SelectItem>{buildings.map(b => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Status</Label>
              <Select value={form.status} onValueChange={v => setForm({ ...form, status: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{STATUSES.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Linked LPO #</Label><Input value={form.related_lpo || ''} onChange={e => setForm({ ...form, related_lpo: e.target.value })} placeholder="LPO/2026/0042" /></div>
            <div><Label>Linked Work Order #</Label><Input value={form.related_wo || ''} onChange={e => setForm({ ...form, related_wo: e.target.value })} placeholder="WO/INT/..." /></div>
            <div><Label>Scheduled start</Label><Input type="datetime-local" value={form.scheduled_start || ''} onChange={e => setForm({ ...form, scheduled_start: e.target.value })} /></div>
            <div><Label>Scheduled end</Label><Input type="datetime-local" value={form.scheduled_end || ''} onChange={e => setForm({ ...form, scheduled_end: e.target.value })} /></div>
          </div>

          {/* Assignee */}
          <div className="border rounded-lg p-3 space-y-3">
            <Label>Assigned to</Label>
            <div className="flex gap-2">
              <Button size="sm" variant={form.assignee_type === 'staff' ? 'default' : 'outline'} onClick={() => setForm({ ...form, assignee_type: 'staff', assigned_to_company: '' })}>Internal staff</Button>
              <Button size="sm" variant={form.assignee_type === 'subcontractor' ? 'default' : 'outline'} onClick={() => setForm({ ...form, assignee_type: 'subcontractor', assigned_to_id: '' })}>Subcontractor</Button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              {form.assignee_type === 'staff' ? (
                <div className="md:col-span-3"><Label>Staff member</Label>
                  <Select value={form.assigned_to_id || 'none'} onValueChange={v => { const u = staff.find(s => s.id === v); setForm({ ...form, assigned_to_id: v === 'none' ? '' : v, assigned_to_name: u?.name || '', assigned_to_phone: u?.phone || '' }); }}>
                    <SelectTrigger data-testid="jo-staff-select"><SelectValue placeholder="Pick a staff" /></SelectTrigger>
                    <SelectContent><SelectItem value="none">Unassigned</SelectItem>{staff.map(s => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              ) : (
                <>
                  <div><Label>Contact name *</Label><Input data-testid="jo-subcontractor-name" value={form.assigned_to_name || ''} onChange={e => setForm({ ...form, assigned_to_name: e.target.value })} /></div>
                  <div><Label>Company</Label><Input value={form.assigned_to_company || ''} onChange={e => setForm({ ...form, assigned_to_company: e.target.value })} /></div>
                  <div><Label>Phone</Label><Input value={form.assigned_to_phone || ''} onChange={e => setForm({ ...form, assigned_to_phone: e.target.value })} /></div>
                </>
              )}
            </div>
          </div>

          {/* Work Types */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <Label>Work types</Label>
              <div className="flex gap-2">
                <Button data-testid="jo-load-template" size="sm" variant="outline" onClick={loadTemplate} disabled={!form.work_types.length}>Load template</Button>
                <Button data-testid="jo-ai-tweak" size="sm" onClick={aiTweak} disabled={!form.work_types.length || aiLoading} className="bg-orange-500 hover:bg-orange-600">
                  <Sparkles className="w-4 h-4 mr-1" /> {aiLoading ? 'Thinking…' : 'AI tweak'}
                </Button>
              </div>
            </div>
            <div className="flex flex-wrap gap-2">
              {WORK_TYPES.map(wt => (
                <button key={wt} type="button" onClick={() => toggleWorkType(wt)} className={`text-xs px-3 py-1.5 rounded-full border ${form.work_types.includes(wt) ? 'bg-orange-500 text-white border-orange-500' : 'bg-card text-foreground border-border'}`}>{wt.replace(/_/g, ' ')}</button>
              ))}
            </div>
          </div>

          {/* Safety + MoS */}
          <div><Label>Safety instructions</Label><Textarea data-testid="jo-safety" rows={3} value={form.safety_instructions || ''} onChange={e => setForm({ ...form, safety_instructions: e.target.value })} /></div>
          <div><Label>Method of Statement</Label><Textarea data-testid="jo-mos" rows={5} value={form.method_of_statement || ''} onChange={e => setForm({ ...form, method_of_statement: e.target.value })} /></div>

          {/* Risk Matrix */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <Label>Risk Assessment (5×5 matrix)</Label>
              <Button data-testid="jo-add-risk-row" size="sm" variant="outline" onClick={addRiskRow}><Plus className="w-3.5 h-3.5 mr-1" />Add row</Button>
            </div>
            <div className="space-y-2">
              {(form.risk_assessment || []).length === 0 && <div className="text-xs text-muted-foreground italic">No rows. Click &ldquo;Load template&rdquo; or &ldquo;AI tweak&rdquo; to populate.</div>}
              {(form.risk_assessment || []).map((r, i) => (
                <Card key={i} data-testid={`jo-risk-${i}`} className="p-3 space-y-2">
                  <div className="grid grid-cols-1 md:grid-cols-12 gap-2">
                    <div className="md:col-span-4"><Label className="text-xs">Hazard</Label><Input value={r.hazard || ''} onChange={e => updateRisk(i, 'hazard', e.target.value)} /></div>
                    <div className="md:col-span-1"><Label className="text-xs">L</Label><Input type="number" min={1} max={5} value={r.likelihood || 1} onChange={e => updateRisk(i, 'likelihood', e.target.value)} /></div>
                    <div className="md:col-span-1"><Label className="text-xs">S</Label><Input type="number" min={1} max={5} value={r.severity || 1} onChange={e => updateRisk(i, 'severity', e.target.value)} /></div>
                    <div className="md:col-span-1"><Label className="text-xs">Risk</Label>
                      <div className={`mt-2 px-2 py-1 rounded text-center text-xs font-bold ${riskCellColor(r.risk_score || (r.likelihood || 1) * (r.severity || 1))}`}>{r.risk_score || (r.likelihood || 1) * (r.severity || 1)}</div>
                    </div>
                    <div className="md:col-span-4"><Label className="text-xs">Control measures</Label><Input value={r.control_measures || ''} onChange={e => updateRisk(i, 'control_measures', e.target.value)} /></div>
                    <div className="md:col-span-1 flex items-end"><Button size="sm" variant="ghost" onClick={() => removeRiskRow(i)} className="text-rose-600"><Trash2 className="w-3.5 h-3.5" /></Button></div>
                    <div className="md:col-span-1"><Label className="text-xs">Res L</Label><Input type="number" min={1} max={5} value={r.residual_likelihood || 1} onChange={e => updateRisk(i, 'residual_likelihood', e.target.value)} /></div>
                    <div className="md:col-span-1"><Label className="text-xs">Res S</Label><Input type="number" min={1} max={5} value={r.residual_severity || 1} onChange={e => updateRisk(i, 'residual_severity', e.target.value)} /></div>
                    <div className="md:col-span-1"><Label className="text-xs">Residual</Label>
                      <div className={`mt-2 px-2 py-1 rounded text-center text-xs font-bold ${riskCellColor(r.residual_score || (r.residual_likelihood || 1) * (r.residual_severity || 1))}`}>{r.residual_score || (r.residual_likelihood || 1) * (r.residual_severity || 1)}</div>
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          </div>

          {/* Checklist */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <Label>Completion checklist</Label>
              <Button size="sm" variant="outline" onClick={addChecklistItem}><Plus className="w-3.5 h-3.5 mr-1" />Add item</Button>
            </div>
            <div className="space-y-1">
              {(form.completion_checklist || []).map((c, i) => (
                <div key={i} className="flex items-center gap-2">
                  <input type="checkbox" checked={!!c.done} onChange={e => updateChecklistItem(i, 'done', e.target.checked)} />
                  <Input value={c.name || ''} onChange={e => updateChecklistItem(i, 'name', e.target.value)} placeholder="Checklist item" />
                </div>
              ))}
            </div>
          </div>

          {/* Photos */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <PhotoPanel title="Before photos" photos={form.before_photos || []} onAdd={e => onPhoto(e, 'before_photos')} onRemove={i => removePhoto('before_photos', i)} testId="before" />
            <PhotoPanel title="After photos" photos={form.after_photos || []} onAdd={e => onPhoto(e, 'after_photos')} onRemove={i => removePhoto('after_photos', i)} testId="after" />
          </div>
        </div>
        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={onClose}>Close</Button>
          <Button data-testid="jo-save-btn" onClick={save} disabled={saving}>{saving ? 'Saving…' : (isEdit ? 'Save changes' : 'Create Job Order')}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function PhotoPanel({ title, photos, onAdd, onRemove, testId }) {
  return (
    <Card className="p-3">
      <div className="flex items-center justify-between mb-2">
        <Label>{title}</Label>
        <label className="cursor-pointer text-xs text-orange-600 hover:underline inline-flex items-center gap-1">
          <ImagePlus className="w-3.5 h-3.5" /> Add
          <input data-testid={`jo-${testId}-photos`} type="file" multiple accept="image/*" className="hidden" onChange={onAdd} />
        </label>
      </div>
      {photos.length === 0 ? <div className="text-xs text-muted-foreground italic">No photos.</div> :
        <div className="grid grid-cols-3 gap-2">
          {photos.map((p, i) => (
            <div key={i} className="relative aspect-square">
              <img src={p} alt={`${title} ${i}`} className="w-full h-full object-cover rounded" />
              <button onClick={() => onRemove(i)} className="absolute -top-1 -right-1 bg-rose-600 text-white rounded-full w-5 h-5 text-xs">×</button>
            </div>
          ))}
        </div>}
    </Card>
  );
}
